/**
 * Client-side downscaling for fabric image uploads.
 *
 * Designers hand us print-resolution art — vendor swatch exports run 60-120 MB.
 * The API re-encodes everything it receives anyway (a swatch-profile JPEG at
 * 800px and a texture-profile JPEG at 2048px), so every byte above what those
 * profiles consume is uploaded, buffered, and then thrown away. On office wifi a
 * 118 MB POST is minutes of waiting per fabric for output that is identical to
 * what a ~1 MB POST produces.
 *
 * So we shrink first and upload the small version. This is an OPTIMIZATION, not a
 * correctness dependency: the server still runs its own authoritative encode, and
 * every failure path here falls back to the original File. A browser that can't
 * decode the image, an OffscreenCanvas that won't allocate, a codec that returns
 * nothing — all of them just mean "upload what the user picked", which is exactly
 * the behaviour we had before.
 *
 * MAX_EDGE is deliberately the API's TEXTURE profile size, not its swatch size:
 * the same picked file can be sent to either endpoint, and handing the texture
 * pipeline a pre-shrunk 800px source would permanently cap the 3D tile's detail.
 * Matching the larger of the two consumers keeps the server's own resize as the
 * only thing that decides final dimensions.
 */

/** Longest edge we upload. Matches TEXTURE_PROFILE.maxPx in the API. */
const MAX_EDGE = 2048;

/** Re-encode quality. Higher than the server's, since the server encodes again. */
const QUALITY = 0.9;

/**
 * Below this, downscaling isn't worth the decode: the upload is already cheap and
 * a re-encode would only add a generation of JPEG loss for no meaningful savings.
 */
const SKIP_BELOW_BYTES = 1.5 * 1024 * 1024;

/** Swap any extension for .jpg — the re-encoded blob is always a JPEG. */
function toJpgName(name: string): string {
  return name.replace(/\.[^.]+$/, "") + ".jpg";
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
}

/**
 * Return a downscaled JPEG File (long edge <= MAX_EDGE), or the original File if
 * it is already small enough, or if anything at all goes wrong.
 */
export async function downscaleForUpload(file: File): Promise<File> {
  if (!file.type.startsWith("image/")) return file;
  if (file.size <= SKIP_BELOW_BYTES) return file;

  let bitmap: ImageBitmap | undefined;
  try {
    // createImageBitmap decodes off the main thread, so a 100 MP source doesn't
    // freeze the UI while it is being read.
    bitmap = await createImageBitmap(file);

    const { width, height } = bitmap;
    const scale = Math.min(1, MAX_EDGE / Math.max(width, height));

    // Already within the pixel cap AND already JPEG: there is nothing left to win.
    // Re-encoding would only spend a generation of lossy compression to arrive at
    // roughly the same size, so hand back the original bytes untouched.
    //
    // Note this does NOT bail out for a big PNG that happens to be within the cap
    // (a losslessly-exported 1800px swatch can still be several MB). That file
    // falls through and gets converted to JPEG at its native dimensions — the
    // format change alone is most of the saving, with no resolution lost.
    if (scale === 1 && file.type === "image/jpeg") return file;

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));

    const ctx = canvas.getContext("2d");
    if (!ctx) return file;

    // Fabric art is continuous-tone, so smoothing is what we want here; the
    // browser's high-quality filter is the closest thing available to the
    // lanczos resample the server does.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // Flatten onto white first: a PNG with alpha would otherwise composite onto
    // transparent-black and darken when JPEG drops the alpha channel. Matches the
    // server's .flatten({ background: white }).
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const blob = await canvasToBlob(canvas, QUALITY);
    if (!blob) return file;

    // A pathological source can encode LARGER than it arrived. Never upload the
    // worse of the two.
    if (blob.size >= file.size) return file;

    return new File([blob], toJpgName(file.name), {
      type: "image/jpeg",
      lastModified: file.lastModified,
    });
  } catch (err) {
    // Undecodable by the browser doesn't mean undecodable by libvips — the API
    // decodes with failOn:"none" and salvages mildly-corrupt vendor exports that
    // browsers refuse. Hand the original over and let the server try.
    console.warn("Client-side downscale failed; uploading original file.", err);
    return file;
  } finally {
    if (bitmap) bitmap.close();
  }
}
